import { useState } from "react";
import {
    useGridApiContext,
    useGridSelector,
    gridColumnDefinitionsSelector,
    gridColumnVisibilityModelSelector,
} from "@mui/x-data-grid";
import { Box, InputAdornment, TextField, Typography, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CommonCheckbox from "../../Checkbox/CommonCheckbox";
import "./TableComponent.scss";

const TableColumnsPanel = () => {
    const apiRef = useGridApiContext();
    const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
    const visibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
    const [search, setSearch] = useState("");

    // A column is visible if its key is absent from the model or explicitly true
    const isVisible = (field: string) => visibilityModel[field] !== false;

    // Helper to extract plain text from React elements (JSX)
    const getTextFromJSX = (node: any): string => {
        if (!node) return "";
        if (typeof node === "string" || typeof node === "number") return String(node);
        if (Array.isArray(node)) return node.map(getTextFromJSX).join("");
        if (node.props && node.props.children) return getTextFromJSX(node.props.children);
        return "";
    };

    const getColumnLabel = (col: (typeof columns)[0]): string => {
        if (col.headerName) return col.headerName;
        if (col.renderHeader) {
            const headerParams: any = {
                field: col.field,
                colDef: col,
                api: apiRef.current,
            };
            return getTextFromJSX(col.renderHeader(headerParams));
        }
        return col.field;
    };

    const filtered = columns.filter(
        (col) =>
            !col.disableColumnMenu &&
            getColumnLabel(col).toLowerCase().includes(search.toLowerCase())
    );

    const toggleColumn = (field: string, label: string) => {
        if (label.toLowerCase().trim() === "s.no") return;
        apiRef.current.setColumnVisibility(field, !isVisible(field));
    };

    const showAll = () =>
        columns.forEach((col) => apiRef.current.setColumnVisibility(col.field, true));

    const hideAll = () =>
        columns.forEach((col) => {
            const label = getColumnLabel(col);
            if (!col.disableColumnMenu && label.toLowerCase().trim() !== "s.no") {
                apiRef.current.setColumnVisibility(col.field, false);
            }
        });

    return (
        <Box className="custom-columns-panel">
            {/* Search */}
            <Box className="ccp-search">
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Find column"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "#9ba8b5" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "13px",
                            "& fieldset": { borderColor: "#e5eaf0" },
                            "&:hover fieldset": { borderColor: "#1a73e8" },
                            "&.Mui-focused fieldset": { borderColor: "#1a73e8", borderWidth: "1.5px" },
                        },
                    }}
                />
            </Box>

            {/* Column list */}
            <Box className="ccp-list">
                {filtered.map((col) => {
                    const headerParams: any = {
                        field: col.field,
                        colDef: col,
                        api: apiRef.current,
                    };
                    const label = col.renderHeader
                        ? col.renderHeader(headerParams)
                        : getColumnLabel(col);
                    const visible = isVisible(col.field);
                    const isSNo = getColumnLabel(col).toLowerCase().trim() === "s.no";
                    return (
                        <Box
                            key={col.field}
                            className={`ccp-row ${visible ? "ccp-row--visible" : ""} ${isSNo ? "ccp-row--disabled" : ""}`}
                            onClick={() => !isSNo && toggleColumn(col.field, getColumnLabel(col))}
                            sx={{
                                cursor: isSNo ? "default" : "pointer",
                                opacity: isSNo ? 0.6 : 1,
                            }}
                        >
                            <CommonCheckbox
                                size="small"
                                color="primary"
                                checked={visible || isSNo}
                                onChange={() => !isSNo && toggleColumn(col.field, getColumnLabel(col))}
                                sx={{ padding: 0 }}
                                disabled={isSNo}
                            />
                            <Typography className="ccp-label">{label}</Typography>
                        </Box>
                    );
                })}
                {filtered.length === 0 && (
                    <Typography className="ccp-empty">No columns found</Typography>
                )}
            </Box>

            {/* Footer actions */}
            <Box className="ccp-footer">
                <Button size="small" onClick={hideAll} className="ccp-btn ccp-btn--muted">
                    Hide All
                </Button>
                <Button size="small" onClick={showAll} className="ccp-btn ccp-btn--primary">
                    Show All
                </Button>
            </Box>
        </Box>
    );
};

export default TableColumnsPanel;
